import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Shared WhatsApp quick-auth + new-user registration block.
 * Rendered inline inside QuickOfferPopup AND ProductDetailsPopup so a new
 * customer never has to go through a second pop-up to register/login.
 */
export const IdentityFields = ({
  sessionWhatsapp, maskedUsername, whatsapp, setWhatsapp, checkingWhatsapp,
  userExists, whatsappError, username, setUsername,
  confirmWhatsappChecked, setConfirmWhatsappChecked, testidPrefix = "offer"
}) => {
  return (
    <div className="space-y-4">
      {/* WhatsApp Field - skipped for users already logged in with a known number */}
      {sessionWhatsapp ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid={`${testidPrefix}-session-whatsapp`}>
          <p className="text-sm text-green-700 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Enviando como <span className="font-semibold">{maskedUsername}</span> · WhatsApp terminado en {sessionWhatsapp.slice(-4)}
          </p>
        </div>
      ) : (
        <div>
          <Label htmlFor={`${testidPrefix}-whatsapp`}>WhatsApp</Label>
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 bg-[#34D399] text-white px-3 py-2 rounded-l-md text-sm font-medium">
              +507
            </div>
            <div className="relative flex-1">
              <Input
                id={`${testidPrefix}-whatsapp`}
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Ej: 61234567"
                className="rounded-l-none"
                maxLength={8}
                data-testid={`${testidPrefix}-whatsapp`}
              />
              {checkingWhatsapp && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#64748B]" />
              )}
              {userExists === true && (
                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
            </div>
          </div>
          <p className="text-xs text-[#64748B] mt-1">
            Este número será tu contraseña para ingresar a tu cuenta
          </p>
          {whatsappError && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle size={12} />
              {whatsappError}
            </p>
          )}
        </div>
      )}

      {/* User exists indicator (only relevant when the WhatsApp field above is shown) */}
      {!sessionWhatsapp && userExists === true && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-700">
            <CheckCircle2 className="inline h-4 w-4 mr-1" />
            Usuario encontrado: <span className="font-semibold">{maskedUsername}</span>
          </p>
        </div>
      )}

      {/* New User Registration Fields */}
      {userExists === false && (
        <div className="space-y-4 border-t border-[#E2E8F0] pt-4">
          <p className="text-sm text-[#0F172A] font-medium">
            Eres nuevo, completa tu registro:
          </p>
          <div>
            <Label htmlFor={`${testidPrefix}-username`}>
              Usuario <span className="text-[#64748B] text-xs">(tú lo eliges)</span>
            </Label>
            <Input
              id={`${testidPrefix}-username`}
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder="Ej: juanperez"
              maxLength={20}
              data-testid={`${testidPrefix}-username`}
            />
            <p className="text-xs text-[#64748B] mt-1">
              Solo letras, números, guiones y guiones bajos
            </p>
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id={`${testidPrefix}-confirm-whatsapp`}
              checked={confirmWhatsappChecked}
              onCheckedChange={setConfirmWhatsappChecked}
              className="mt-0.5"
              data-testid={`${testidPrefix}-confirm-whatsapp`}
            />
            <label htmlFor={`${testidPrefix}-confirm-whatsapp`} className="text-sm text-[#64748B] leading-tight cursor-pointer">
              He revisado mi número de WhatsApp
            </label>
          </div>
        </div>
      )}

      {/* Terms Box - informative only, sending implies acceptance */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`${testidPrefix}-terms`}>
        <p className="text-sm text-amber-900 font-medium leading-tight">
          Al enviar aceptas las condiciones:
        </p>
        <ul className="text-xs text-amber-800 mt-1 space-y-0.5">
          <li>• El precio enviado <strong>no será negociable</strong></li>
          <li>• Clueer <strong>no solicitará pagos</strong> hasta que revises tu producto</li>
        </ul>
      </div>
    </div>
  );
};

export default IdentityFields;
